import { flatten, mapAsync, mapValues, partition, pick, uniq, uniqWith } from 'es-toolkit';

import { getMostRecentAnalysis } from '@paragon/client-tools';
import {
  AnalysisHistoryRecord,
  AnalysisSet,
  AnalysisSettings,
  ComponentDesign,
  ComponentDesignWithInformation,
  FeaturePatternTrussSettings,
  formatLumberSpecies,
  JackGirderConnection,
  JobType,
  LayoutModel,
  LoadSettings,
  Lumber,
  materialSettingsLumberProperties,
  MaterialSettingsPair,
  Member,
  ModelCategory,
  PlatePair,
  PlateType,
  ProfileDefaults,
  ScheduleLineGroup,
  ScheduleLineGroupData,
  Station,
} from '@paragon/contract-library';
import { AspNetStatusCodeResult, designService, sealingService } from '@paragon/data-connections';
import {
  componentDesignsDeleted,
  componentDesignToLayoutModel,
  loadProjectAndComponentDesignLayoutModels,
  nonSynchronizedLayoutModelLoaded,
} from '@paragon/layout-models';
import {
  getLoggedInUser,
  getLoggedInUserOrganization,
  hasPricingPermission,
  shouldSynchronizeComponent,
} from '@paragon/login-tools';
import {
  getLumberCode,
  getPlateTypeString,
  gradeMatches,
  isComboPlateType,
  isPlateAnalysisSupported,
  loadSecondaryOrganizationMaterialSet,
  lumbersAreEqual,
  speciesMatches,
} from '@paragon/material-lists';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { makeIdSafe } from '@paragon/sync-client';
import { addSpinnerMessage, removeSpinnerMessage, runWithSpinner } from '@paragon/task-spinner';
import { pickBy } from '@paragon/utilities';

import { loadCaseOrCheckResultOverCapacity } from '../../library/loadCaseOrCheckResultOverCapacity';
import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { isConsideredDesignItem, isConsideredLayoutItem } from '../../library/selection/types';
import { getAppMonitor } from '../../monitor';
import { getSyncClient } from '../../syncClientManager';
import { focusCameraOnSelectedObjectOrFallback, focusCameraOnSelectedObjects } from '../../three/ThreeJsSceneActions';
import { getNextComponentDesign } from '../selectors/componentNavigationSelectors';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getActiveStation } from '../selectors/getActiveStation';
import { getSingleSelectedItemEntryOrNull } from '../selectors/getSelectedItemSelector';
import { getSortedComponentDesigns } from '../selectors/getSortedComponentDesignsSelectors';
import { isLinkUser } from '../selectors/isLinkUser';
import { isLayoutModeEnabled } from '../selectors/layoutModeSelectors';
import { getOrderedProductionComponentInstancesForActiveScheduleLineGroup } from '../selectors/scheduleStationSelectors';
import { getAnalysisSettings, getLoadSettings } from '../selectors/settingsDatabaseSelectors';
import { createDoWorkingChangesExistOnComponentDesign } from '../selectors/workingChangesSelectors';
import { setAnalysisHistory, setAnalysisHistoryRecord } from '../slices/analysisHistorySlice';
import { analysisSetsPush, analysisSetsSetActiveGuid } from '../slices/analysisSetsSlice';
import { setRunPlateMislocations } from '../slices/applicationSlice';
import { onCompletingAsyncRequest, onInitiatingLoadComponentDesignRequest } from '../slices/asyncRequestsSlice';
import {
  markComponentDesignAsFullyLoaded,
  receiveComponentDesigns,
  setComponentDesignWithoutInvalidation,
  setDimensionSetGuid,
} from '../slices/componentDesignsSlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import { setDimensionSets } from '../slices/dimensionsSlice';
import { setHangerConfigurations } from '../slices/hangerConfigurationsSlice';
import {
  setHangerPriceRequested,
  setLumberPriceRequested,
  setPlatePriceRequested,
  setPriceForHanger,
  setPricePerPoundForPlateType,
  setPricesForLumber,
} from '../slices/inventoryPricesSlice';
import { loadJackGirderConnections } from '../slices/jackGirderConnectionsSlice';
import { layoutSlice } from '../slices/layoutSlice';
import { setLoadSets } from '../slices/loadSetsSlice';
import {
  getLumberPropertySetKey,
  setLumberPropertySetFailed,
  setLumberPropertySetForLumber,
  setLumberPropertySetRequested,
  shouldMakeLumberPropertySetRequest,
} from '../slices/lumberPropertySetsSlice';
import {
  setPlateLateralResistanceValues,
  setPlateLateralResistanceValuesRequested,
  setPlateLateralResistanceValuesRequestedFailed,
  setPlateMaterialPropertySetForPlateType,
  setPlateMaterialPropertySetRequested,
  setPlateTypeProperties,
  setPlateTypePropertiesRequested,
  shouldMakePlateTypeLateralResistanceValuesRequest,
} from '../slices/platePropertiesSlice';
import {
  loadAnalysisSetGuidToComponentDesignGuid,
  scheduleGroupLineDataLoaded,
  scheduleLineGroupsLoaded,
} from '../slices/productionModelsSlice';
import { setAnalysisSettingsFromObject, setLoadSettingsFromObject } from '../slices/settingsDatabaseSlice';
import { loadStation } from '../slices/stationsSlice';
import { trussGroupsLoaded } from '../slices/trussGroupsSlice';
import { setTemporaryColorBy } from '../slices/visualOptionsSlice';
import { showChooseProjectDialog, showPartsDialog, showSelectScheduleLineGroupDialog } from './popupActions';
import { selectItem, selectItemByGuid, selectLoadCase, selectLoadCaseInUrlOrDefault } from './selectActions';
import {
  setAnalysisSettingsForComponentDesigns,
  setFeaturePatternSettingsForComponentDesigns,
  setLoadSettingsForComponentDesigns,
  setMaterialSettingsForComponentDesigns,
  setProfileDefaultsForComponentDesigns,
} from './settingsActions';
import { displaySynchronizationError } from './synchronizationActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';
import { updateUrlFromState } from './urlActions';

export const MAX_CONCURRENT_COMPONENT_DESIGN_LOADS = 6;

const loadLumberPropertySetForLumber =
  (lumber: Lumber): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const lumberPropertySetKey = getLumberPropertySetKey(lumber);
    if (!shouldMakeLumberPropertySetRequest(getState().lumberPropertySets, lumberPropertySetKey)) {
      return;
    }

    dispatch(setLumberPropertySetRequested(lumber));

    const lumberPropertySet = await designService.lumbers.getLatestCalculationPropertySet(lumber);
    if (lumberPropertySet) {
      dispatch(
        setLumberPropertySetForLumber({
          ...lumber,
          lumberPropertySet,
        }),
      );
    } else {
      dispatch(setLumberPropertySetFailed(lumber));
    }
  };

export const loadAdditionalLumberInformation =
  (lumber: Lumber): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const isLumberPropertySetLoaded = getState().lumberPropertySets.lumberPropertySets.some(
      (set) =>
        speciesMatches(set, lumber) &&
        gradeMatches(set, lumber) &&
        set.treatmentType === lumber.treatmentType &&
        set.nominalThickness === lumber.nominalThickness &&
        set.nominalWidth === lumber.nominalWidth,
    );

    if (!isLumberPropertySetLoaded) {
      await dispatch(loadLumberPropertySetForLumber(lumber));
    }
  };

export const loadPlateMaterialPropertySetForPlateType =
  (plateType: PlateType, tangiblePlateTypesForComboPlateType: readonly PlateType[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const getPlateTypeStringResult = getPlateTypeString(plateType, tangiblePlateTypesForComboPlateType);

    if (!getState().plateProperties.plateMaterialPropertySetRequests[getPlateTypeStringResult]) {
      dispatch(setPlateMaterialPropertySetRequested({ getPlateTypeStringResult }));
      const propertySet = await designService.platePairs.getLatestCalculationPropertySet(
        plateType,
        tangiblePlateTypesForComboPlateType,
      );
      dispatch(setPlateMaterialPropertySetForPlateType({ getPlateTypeStringResult, propertySet }));
    }
  };

export const loadAdditionalPlatePairInformation =
  (platePairs: ReadonlyArray<PlatePair>): AsyncThunkActionVoid =>
  async (dispatch) => {
    const plateTypesWithTangiblePlateTypes = getAnalyzablePlateTypesWithTangiblePlateTypes(platePairs);
    const plateMaterialPropertySetPromises = plateTypesWithTangiblePlateTypes.map(
      ({ plateType, tangiblePlateTypesForComboPlateType }) =>
        dispatch(loadPlateMaterialPropertySetForPlateType(plateType, tangiblePlateTypesForComboPlateType)),
    );
    const plateTypePropertiesPromises = plateTypesWithTangiblePlateTypes.map(
      ({ plateType, tangiblePlateTypesForComboPlateType }) =>
        dispatch(loadPlateTypeProperties(plateType, tangiblePlateTypesForComboPlateType)),
    );
    await Promise.all(plateMaterialPropertySetPromises.concat(plateTypePropertiesPromises));
  };

function getAnalyzablePlateTypesWithTangiblePlateTypes(platePairs: readonly PlatePair[]) {
  const [comboPlatePairs, regularPlatePairs] = partition(platePairs, ({ plateType }) => isComboPlateType(plateType));
  const plateTypesWithTangiblePlateTypes = regularPlatePairs
    .filter((regularPlatePair) => isPlateAnalysisSupported(regularPlatePair))
    .map((regularPlatePair) => ({
      plateType: regularPlatePair.plateType,
      tangiblePlateTypesForComboPlateType: [] as readonly PlateType[],
    }));
  const comboPlateTypeIdentifiers: string[] = [];
  for (const comboPlatePair of comboPlatePairs) {
    const comboPlateTypeIdentifier = getPlateTypeString(
      comboPlatePair.plateType,
      comboPlatePair.tangiblePlateTypesForComboPlateType,
    );
    if (!comboPlateTypeIdentifiers.includes(comboPlateTypeIdentifier)) {
      comboPlateTypeIdentifiers.push(comboPlateTypeIdentifier);
      plateTypesWithTangiblePlateTypes.push({
        plateType: comboPlatePair.plateType,
        tangiblePlateTypesForComboPlateType: comboPlatePair.tangiblePlateTypesForComboPlateType,
      });
    }
  }

  return plateTypesWithTangiblePlateTypes;
}

export const loadPlateLateralResistanceCheck =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const organization = componentDesign.organization;
    const plateTypesWithTangiblePlateTypes = getAnalyzablePlateTypesWithTangiblePlateTypes(
      componentDesign.component.platePairs,
    );
    const lumberSpeciesAndTreatmentTypes = uniqWith(
      componentDesign.component.members.map((member) => ({
        species: member.lumber.species,
        tangibleSpecies: member.lumber.tangibleSpecies,
        treatmentType: member.lumber.treatmentType,
      })),
      (a, b) =>
        a.species === b.species &&
        a.tangibleSpecies.length === b.tangibleSpecies.length &&
        a.tangibleSpecies.every((species) => b.tangibleSpecies.includes(species)) &&
        a.treatmentType === b.treatmentType,
    );

    for (const { plateType, tangiblePlateTypesForComboPlateType } of plateTypesWithTangiblePlateTypes) {
      for (const { species, tangibleSpecies, treatmentType } of lumberSpeciesAndTreatmentTypes) {
        const key = `${plateType}-${formatLumberSpecies(species, tangibleSpecies)}-${treatmentType}-${organization}`;

        if (shouldMakePlateTypeLateralResistanceValuesRequest(getState().plateProperties, key)) {
          dispatch(setPlateLateralResistanceValuesRequested(key));

          try {
            const lateralResistanceSet = await designService.platePairs.getPlateGripSummary(
              plateType,
              tangiblePlateTypesForComboPlateType,
              species,
              tangibleSpecies,
              treatmentType,
              organization,
            );
            if (lateralResistanceSet) {
              dispatch(setPlateLateralResistanceValues({ key, lateralResistanceSet }));
            } else {
              dispatch(setPlateLateralResistanceValuesRequestedFailed(key));
            }
          } catch (err) {
            // continue regardless of error (not sure why, I didn't add this - David)
            dispatch(setPlateLateralResistanceValuesRequestedFailed(key));
          }
        }
      }
    }
  };

const loadPlateTypeProperties =
  (plateType: PlateType, tangiblePlateTypesForComboPlateType: readonly PlateType[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const getPlateTypeStringResult = getPlateTypeString(plateType, tangiblePlateTypesForComboPlateType);
    if (!getState().plateProperties.plateTypePropertiesRequests[getPlateTypeStringResult]) {
      dispatch(setPlateTypePropertiesRequested({ getPlateTypeStringResult }));
      const plateTypeProperties = await designService.platePairs.getPlateTypeProperties(
        plateType,
        tangiblePlateTypesForComboPlateType,
      );
      dispatch(setPlateTypeProperties({ plateTypeProperties, getPlateTypeStringResult }));
    }
  };

export const loadHangerConfigurations = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  if (getState().hangerConfigurations.length === 0) {
    const hangerConfigurations = await designService.hangerConnectors.getAllHangerConfigurations();
    dispatch(setHangerConfigurations(hangerConfigurations));
  }
};

export const unloadInsertComponentDesign =
  (componentDesign: ComponentDesign): ThunkActionVoid =>
  (dispatch) => {
    // This does not unload data about lumber, etc. that is not used by other components.
    dispatch(componentDesignsDeleted(componentDesign.guid));
  };

export const loadComponentDesignByGuid =
  (
    componentDesignGuid: string,
    options: {
      // If set, the loaded component design will not be stored in redux state.  It will still be returned, and its
      //   history and timestamps will still be stored in redux state.
      skipSettingComponentDesign?: boolean;

      skipJackGirderConnections: boolean;
      muteErrorNotifications?: boolean;
    },
  ): AsyncThunkActionReturn<ComponentDesignWithInformation | undefined> =>
  async (dispatch, getState) => {
    const requestGuid = crypto.randomUUID();
    dispatch(onInitiatingLoadComponentDesignRequest({ componentDesignGuid, requestGuid }));

    try {
      const { openContext } = getState().application;
      const sharedComponentSetLookupCode =
        openContext?.type === 'shared-component-set' ? openContext.sharedComponentSetLookupCode : undefined;
      const componentDesignWithInformation = await designService.componentDesigns.fetch(
        componentDesignGuid,
        sharedComponentSetLookupCode,
      );
      const { componentDesign, history, timestamps } = componentDesignWithInformation;

      await dispatch(loadMaterialsForOtherOrganizationIfNecessary(componentDesign));

      if (!options.skipSettingComponentDesign) {
        await dispatch(
          receiveComponentDesign(componentDesign, {
            commitComponentDesign: true,
            replaceWorkingChanges: true,
            isPersisted: true,
            skipJackGirderConnections: options.skipJackGirderConnections,
          }),
        );
      }

      dispatch(setAnalysisHistory(history));
      dispatch(setComponentDesignTimestamps({ timestamps }));
      return componentDesignWithInformation;
    } catch (error) {
      console.error(error);
      if (!options.muteErrorNotifications) {
        dispatch(enqueueError(`Could not load component ${componentDesignGuid}.`));
      }

      return undefined;
    } finally {
      dispatch(onCompletingAsyncRequest({ componentDesignGuid, requestGuid }));
    }
  };

export const fetchAndLoadJackGirderConnections =
  (componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;
    const sharedComponentSetLookupCode =
      openContext?.type === 'shared-component-set' ? openContext.sharedComponentSetLookupCode : undefined;
    const jackGirderConnections = flatten(
      await Promise.all([
        designService.jackGirderConnections.getJacks(componentDesignGuid, sharedComponentSetLookupCode),
        designService.jackGirderConnections.getGirders(componentDesignGuid, sharedComponentSetLookupCode),
      ]),
    );
    dispatch(loadJackGirderConnections(jackGirderConnections));
  };

export const loadJackGirderConnectionsForProject =
  (projectGuid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    const jackGirderConnections = await designService.jackGirderConnections.getAllForProject(projectGuid);

    dispatch(loadJackGirderConnections(jackGirderConnections));
  };

export const loadAnalysisResponse =
  (
    analysisSet: AnalysisSet,
    analysisHistoryRecord: AnalysisHistoryRecord,
    selectWhenFinished: boolean,
  ): AsyncThunkActionVoid =>
  async (dispatch) => {
    await dispatch(
      receiveAnalysisSet(analysisSet, {
        replaceWorkingChanges: true,
        setAnalysisSetAsActive: selectWhenFinished ?? false,
        skipJackGirderConnections: true,
      }),
    );
    dispatch(setAnalysisHistoryRecord(analysisHistoryRecord));

    if (selectWhenFinished) {
      dispatch(selectItem(analysisSet.componentDesign, 'componentDesign', { force: true, retainNotifications: true }));
      const colorBy = loadCaseOrCheckResultOverCapacity(analysisSet.capacity) ? 'Combined' : 'LumberProperties';
      dispatch(setTemporaryColorBy(colorBy));
      dispatch(selectLoadCase(null));
    }
  };

interface LoadAnalysisSetOptions {
  /** If true, working changes will be replaced no matter what. */
  readonly forceUpdateWorkingChanges: boolean;

  readonly setAnalysisSetAsActive: boolean;
  readonly skipJackGirderConnections: boolean;
  readonly skipSettings?: boolean;
}

export const loadAnalysisSetByGuid =
  (analysisSetGuid: string, options: LoadAnalysisSetOptions): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    try {
      const { openContext } = getState().application;
      const sharedComponentSetLookupCode =
        openContext?.type === 'shared-component-set' ? openContext.sharedComponentSetLookupCode : undefined;
      const analysisSet = await designService.analysisSets.get(analysisSetGuid, sharedComponentSetLookupCode);

      await dispatch(
        receiveAnalysisSet(analysisSet, { ...options, replaceWorkingChanges: options.forceUpdateWorkingChanges }),
      );
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Could not load analysis set ${analysisSetGuid}.`));
    }
  };

export const loadAdditionalComponentDesignData =
  (componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const componentDesign = getState().componentDesigns.data[componentDesignGuid];
    if (!componentDesign) throw new Error('Expected component design to be loaded.');

    dispatch(markComponentDesignAsFullyLoaded(componentDesignGuid));

    try {
      await Promise.all([
        dispatch(loadMaterialsForOtherOrganizationIfNecessary(componentDesign)),
        dispatch(loadAdditionalComponentDesignInformation(componentDesign, { isPersisted: true })),
        dispatch(loadDimensionSet(componentDesign.dimensionSetGuid)),
        dispatch(loadSettings(componentDesign.guid)),
        dispatch(loadMostRecentAnalysisSet(componentDesign.guid)),
      ]);

      if (shouldSynchronizeComponent(getState())) {
        getSyncClient().whenReady.then((client) => client.rooms.enter(`#design-${componentDesign.guid}`));
      }
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Could not load additional data for ${componentDesign.component.name}.`));
    }
  };

const loadMaterialsForOtherOrganizationIfNecessary =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (
      getLoggedInUser(getState()) &&
      componentDesign.organization &&
      componentDesign.organization !== getLoggedInUserOrganization(getState())
    ) {
      await dispatch(loadSecondaryOrganizationMaterialSet(componentDesign.organization));
    }
  };

const loadMostRecentAnalysisSet =
  (componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const history = getState().analysisHistory[componentDesignGuid];
    if (!history) return;

    const mostRecentAnalysis = getMostRecentAnalysis(Object.values(history));
    if (!mostRecentAnalysis) return;

    await dispatch(
      loadAnalysisSetByGuid(mostRecentAnalysis.analysisSetGuid, {
        skipJackGirderConnections: true,
        setAnalysisSetAsActive: false,
        forceUpdateWorkingChanges: false,
      }),
    );
  };

export const loadComponentDesign =
  (
    componentDesignGuid: string,
    options: {
      /** If not supplied, the analysis set will be set as active if there are no working changes. */
      setAnalysisSetAsActive?: boolean;
      skipJackGirderConnections: boolean;
    },
  ): AsyncThunkActionReturn<ComponentDesign | undefined> =>
  async (dispatch, getState) => {
    const componentDesignLoadingPromise = dispatch(
      loadComponentDesignByGuid(componentDesignGuid, { skipJackGirderConnections: options.skipJackGirderConnections }),
    );

    const componentDesignWithHistory = await componentDesignLoadingPromise;
    if (componentDesignWithHistory == null) return;

    const mostRecentAnalysis = getMostRecentAnalysis(Object.values(componentDesignWithHistory.history));

    if (mostRecentAnalysis) {
      await dispatch(
        loadAnalysisSetByGuid(mostRecentAnalysis.analysisSetGuid, {
          ...options,

          setAnalysisSetAsActive:
            options.setAnalysisSetAsActive ??
            !createDoWorkingChangesExistOnComponentDesign(componentDesignGuid)(getState()),
          forceUpdateWorkingChanges: false,
        }),
      );
    }

    return componentDesignWithHistory.componentDesign;
  };

export const loadAdditionalComponentDesignDataWithSpinner =
  (componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch) =>
    dispatch(runWithSpinner('Loading Component...', dispatch(loadAdditionalComponentDesignData(componentDesignGuid))));

export const loadProject =
  (projectGuid: string, activeComponentDesignGuid: string | null | undefined): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Loading project...'));

    const room = `#projects-${makeIdSafe(getLoggedInUserOrganization(getState())!)}`;
    getSyncClient().whenReady.then((client) => client.rooms.enter(room));

    try {
      const project = await designService.projects.fetch(projectGuid);
      const componentDesignInfo = await dispatch(loadProjectAndComponentDesignLayoutModels(project));
      dispatch(trussGroupsLoaded(Object.values(project.trussGroups)));
      dispatch(receiveComponentDesigns(componentDesignInfo));

      const componentDesignGuids = Array.from(
        componentDesignInfo.componentDesigns.map((componentDesign) => componentDesign.guid),
      );

      const loadJackGirderConnectionsPromise = dispatch(loadJackGirderConnectionsForProject(projectGuid));

      let loadFirstComponentDesignPromise = Promise.resolve();

      if (activeComponentDesignGuid && activeComponentDesignGuid !== '0') {
        loadFirstComponentDesignPromise = dispatch(
          loadActiveComponentDesignForCollection(activeComponentDesignGuid, componentDesignGuids, 'project'),
        );
      } else if (componentDesignGuids.length > 0) {
        loadFirstComponentDesignPromise = dispatch(
          loadComponentDesign(componentDesignGuids[0], {
            setAnalysisSetAsActive: false,
            skipJackGirderConnections: true,
          }),
        ).then((componentDesign) => {
          if (componentDesign && getSelectedItemIdentifiers(getState()).length === 0)
            dispatch(selectItem(componentDesign, 'componentDesign'));
        });
      }

      const loadLayoutPromise = dispatch(loadLayoutData(projectGuid));

      await Promise.all([loadJackGirderConnectionsPromise, loadFirstComponentDesignPromise, loadLayoutPromise]);

      dispatch(ensureCorrectSelectionAfterProjectLoad(projectGuid));
    } catch (error) {
      console.error(error);
      getAppMonitor().recordInteraction('Project load failed');

      // The requested project could not be opened, so offer the user something they can open instead.  That dialog
      //   states the problem itself, so it needs no notification -- which its backdrop would cover anyway.
      if (getLoggedInUser(getState()) && !isLinkUser(getState())) {
        dispatch(showChooseProjectDialog({ reason: 'project-load-failed' }));
      } else {
        dispatch(
          enqueueError(
            'Error loading project. If it does not belong to your organization, request that they create a Shared Component Set.',
          ),
        );
      }
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const ensureCorrectSelectionAfterProjectLoad =
  (projectGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const selectedEntry = getSingleSelectedItemEntryOrNull(getState());

    if (isLayoutModeEnabled(getState())) {
      if (selectedEntry && isConsideredLayoutItem(selectedEntry.type)) {
        // this case is for the purpose of visual synchronization, so we must `forceSelect` and focus the camera too
        dispatch(selectItem(selectedEntry.item, selectedEntry.type, { force: true }));
      } else if (!selectedEntry) {
        // Handle the case when nothing is selected due to an empty URL. Normally when this happens the first loaded
        //   component design will be selected when it loads, so this is only for undesigned layouts.
        dispatch(selectItemByGuid(projectGuid, 'project', { force: true }));
      }
    }

    focusCameraOnSelectedObjectOrFallback();
  };

export const loadLayoutData =
  (projectGuid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    const layout = await designService.projects.getLayout(projectGuid);

    const roofContainersWithNoSolidData = Object.values(layout.roofContainers).filter(
      (roofContainer) => !roofContainer.solidData,
    );
    if (roofContainersWithNoSolidData.length === 1) {
      dispatch(
        enqueueWarning(`Roof container ${roofContainersWithNoSolidData[0].name} has no solid associated with it.`),
      );
    } else if (roofContainersWithNoSolidData.length > 1) {
      const roofContainerNames = roofContainersWithNoSolidData.map((roofContainer) => roofContainer.name).join(', ');
      dispatch(enqueueWarning(`Roof containers ${roofContainerNames} have no solid associated with them.`));
    }

    dispatch(layoutSlice.actions.layoutDataLoaded(layout));
  };

export interface JobLayoutModel extends LayoutModel {
  jobType: JobType;
  isParagonJob: boolean;
  sealed: boolean;
  locked: boolean;
  componentDesignGuids: string[];
  ownerOrganization: string;
  sharedWithOrganizations: string[];
}

export const loadJob =
  (jobId: number, activeComponentDesignGuid: string | null | undefined): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const user = getLoggedInUser(getState())!;

    const spinnerId = dispatch(addSpinnerMessage('Loading job...'));

    try {
      const {
        componentDesignGuids,
        isParagonJob,
        jobType,
        locked,
        ownerOrganization,
        sealed,
        sharedWithOrganizations,
      } = await sealingService.jobs.getComponentDesignGuidsAndMetadata(jobId);

      const jobLayoutModel: JobLayoutModel = {
        type: 'job',
        category: ModelCategory.IntermediateModel,
        guid: jobId.toString(),
        name: jobId.toString(),

        jobType,
        isParagonJob,
        sealed,
        locked,
        componentDesignGuids,

        ownerOrganization,
        sharedWithOrganizations,
      };
      dispatch(nonSynchronizedLayoutModelLoaded(jobLayoutModel));

      if (getLoggedInUser(getState()) && ownerOrganization !== user.organization) {
        await dispatch(loadSecondaryOrganizationMaterialSet(ownerOrganization));
      }

      if (jobType === 'Repair') {
        dispatch(setRunPlateMislocations(false));
      }

      // We're going to load the jack girder connections, but not set them in redux state until we can be sure that all
      //   the component designs they reference are loaded.
      const jackGirderConnectionsPromise =
        designService.jackGirderConnections.getAllInvolvingRecursively(componentDesignGuids);

      // "missing component designs" -- this can occur with repair jobs, when one or more component being repaired is part
      //   of a jack/girder connection with a component that is not being repaired.
      const jackGirderConnections = await jackGirderConnectionsPromise;
      const missingComponentDesignGuids = getMissingComponentDesigns(jackGirderConnections, componentDesignGuids);
      const allComponentDesignGuids = [...componentDesignGuids, ...missingComponentDesignGuids];

      const [componentDesigns, timestamps, history] = await Promise.all([
        designService.componentDesigns.bulkLoad(allComponentDesignGuids),
        designService.componentDesigns.bulkLoadTimestamps(allComponentDesignGuids),
        designService.analysisHistory.bulkLoad(componentDesignGuids),
      ]);
      dispatch(receiveComponentDesigns({ componentDesigns, timestamps, history }));
      const componentDesignSummaryLayoutModels = componentDesigns.map((componentDesign) =>
        componentDesignToLayoutModel(componentDesign),
      );
      dispatch(nonSynchronizedLayoutModelLoaded(componentDesignSummaryLayoutModels));

      dispatch(loadJackGirderConnections(jackGirderConnections));

      if (activeComponentDesignGuid != null)
        await dispatch(loadActiveComponentDesignForCollection(activeComponentDesignGuid, componentDesignGuids, 'job'));
    } catch (error) {
      dispatch(enqueueError('Error loading job.'));
      console.error(error);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

function getMissingComponentDesigns(
  jackGirderConnections: readonly JackGirderConnection[],
  componentDesignGuids: readonly string[],
): string[] {
  const initialComponentDesignGuids = new Set(componentDesignGuids);

  const result = new Set<string>();
  for (const connection of jackGirderConnections) {
    if (!initialComponentDesignGuids.has(connection.jackGuid)) {
      result.add(connection.jackGuid);
    }

    if (!initialComponentDesignGuids.has(connection.girderGuid)) {
      result.add(connection.girderGuid);
    }
  }

  return Array.from(result);
}

export const loadAndSynchronizeStation =
  (stationGuid: string): AsyncThunkActionReturn<Station> =>
  async (dispatch) => {
    try {
      await getSyncClient().whenReady.then((client) => client.rooms.enter(`#station-${stationGuid}`));
    } catch (err) {
      dispatch(displaySynchronizationError());
      console.error(err);
    }

    const station = await designService.stations.getStation(stationGuid);
    dispatch(loadStation({ station, setAsActive: true }));

    return station;
  };

export const loadStationAndComponentDesigns =
  (stationGuid: string, activeComponentDesignGuid?: string | null | undefined): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Loading station...'));

    try {
      const loadedStation = await dispatch(loadAndSynchronizeStation(stationGuid));

      switch (loadedStation.role) {
        case 'ChordSaw':
        case 'WebSaw':
        case 'BlockSaw':
        case 'PlatePull':
        case 'Bundler':
          dispatch(showPartsDialog());
          break;
        default:
          if (loadedStation.followsSchedule) dispatch(showSelectScheduleLineGroupDialog());
          break;
      }

      if (loadedStation.followsSchedule) return;

      const componentDesignGuids = Object.keys(loadedStation.componentDesigns);

      if (componentDesignGuids.length === 0) {
        // Normally, the url will be updated when the first component design in the station is selected.  So if the
        //   station is empty we need to update the url manually.
        dispatch(updateUrlFromState());
        return;
      }

      // We don't do the usual optimization of attempting to load the selected component design first here for two
      //   reasons.  Firstly, stations are opened in assembly mode, so loading components is faster and there's less need
      //   for this optimization. Also, we often won't have an initially selected component design, so we need to load
      //   them all in order to determine which component is "first" in the navigation panel and should be selected.
      const [componentDesigns, timestamps, history] = await Promise.all([
        designService.componentDesigns.bulkLoad(componentDesignGuids),
        designService.componentDesigns.bulkLoadTimestamps(componentDesignGuids),
        designService.analysisHistory.bulkLoad(componentDesignGuids),
      ]);
      dispatch(receiveComponentDesigns({ componentDesigns, timestamps, history }));
      const componentDesignSummaryLayoutModels = componentDesigns.map((componentDesign) =>
        componentDesignToLayoutModel(componentDesign),
      );
      dispatch(nonSynchronizedLayoutModelLoaded(componentDesignSummaryLayoutModels));

      const stateAfterLoading = getState();
      const activeComponentDesign = activeComponentDesignGuid
        ? getSortedComponentDesigns(stateAfterLoading).find((design) => design.guid === activeComponentDesignGuid)
        : getNextComponentDesign(stateAfterLoading);

      if (activeComponentDesign) {
        dispatch(selectItem(activeComponentDesign, 'componentDesign'));
      } else {
        console.error('Unable to find loaded component to select.');
        return;
      }
    } catch (error) {
      dispatch(enqueueError('Error loading station.'));
      console.error(error);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export function loadScheduleLineGroupsForActiveStation(
  showSnackbarOnError: boolean = true,
): AsyncThunkActionReturn<boolean> {
  return async function loadScheduleLineGroupsForActiveStationThunkAction(dispatch, getState) {
    const state = getState();
    const activeStation = getActiveStation(state);
    if (!activeStation || !activeStation.followsSchedule) return false;

    try {
      const activeScheduleLineGroupsForStation = await designService.scheduleLineGroups.getActiveScheduleLineGroups(
        activeStation.guid,
      );
      dispatch(scheduleLineGroupsLoaded(activeScheduleLineGroupsForStation));
      return true;
    } catch (error) {
      if (showSnackbarOnError)
        dispatch(
          enqueueError(
            'Error loading schedule groups for active station. Refresh to try again or contact Paragon support.',
          ),
        );
      console.error(error);
      return false;
    }
  };
}

export function loadScheduleLineGroupData(
  scheduleLineGroup: ScheduleLineGroup,
  showSnackbarOnError: boolean = true,
): AsyncThunkActionReturn<ScheduleLineGroupData | null> {
  return async function loadScheduleLineGroupDataThunkAction(dispatch, getState) {
    const activeStation = getActiveStation(getState());
    if (!activeStation || !activeStation.followsSchedule) return null;

    const spinnerId = dispatch(addSpinnerMessage('Loading schedule line group data...'));

    try {
      const scheduleLineGroupData = await designService.scheduleLineGroups.getScheduleLineGroupData(
        scheduleLineGroup.guid,
      );
      dispatch(scheduleGroupLineDataLoaded(scheduleLineGroupData));

      // First initiate loading and selecting the component design that is current for the station, or the first one in
      // the schedule.
      const orderedProductionComponentInstancesForActiveScheduleGroup =
        getOrderedProductionComponentInstancesForActiveScheduleLineGroup(getState());
      if (orderedProductionComponentInstancesForActiveScheduleGroup.length === 0) return null;
      const { currentProductionComponentInstanceGuid } = activeStation;
      const currentProductionComponentInstance = currentProductionComponentInstanceGuid
        ? (orderedProductionComponentInstancesForActiveScheduleGroup.find(
            (instance) => instance.guid === currentProductionComponentInstanceGuid,
          ) ?? null)
        : null;
      const productionComponentInstanceToSelect =
        currentProductionComponentInstance ?? orderedProductionComponentInstancesForActiveScheduleGroup[0];
      const analysisSetGuidForInstanceToSelect = productionComponentInstanceToSelect.analysisSetGuid;
      const firstComponentToLoadPromise = designService.analysisSets
        .fetchComponentsFromAnalysisSets([analysisSetGuidForInstanceToSelect])
        .then((analysisSetGuidToComponentDesign) => {
          const [analysisSetGuid, { componentDesign }] = Object.entries(analysisSetGuidToComponentDesign)[0];
          dispatch(loadAnalysisSetGuidToComponentDesignGuid({ [analysisSetGuid]: componentDesign.guid }));
          return dispatch(
            receiveComponentDesign(componentDesign, {
              commitComponentDesign: true,
              isPersisted: true,
              replaceWorkingChanges: true,
              skipJackGirderConnections: true,
            }),
          );
        })
        .then(() => {
          dispatch(selectItem(productionComponentInstanceToSelect, 'productionComponentInstance'));
        });

      // Load the rest of the components
      const analysisSetGuidsForOtherInstances = uniq(
        orderedProductionComponentInstancesForActiveScheduleGroup
          .map((group) => group.analysisSetGuid)
          .filter((analysisSetGuid) => analysisSetGuid !== analysisSetGuidForInstanceToSelect),
      );
      const analysisSetGuidToComponentDesignForOtherInstances =
        await designService.analysisSets.fetchComponentsFromAnalysisSets(analysisSetGuidsForOtherInstances);
      dispatch(
        loadAnalysisSetGuidToComponentDesignGuid(
          mapValues(analysisSetGuidToComponentDesignForOtherInstances, ({ componentDesign }) => componentDesign.guid),
        ),
      );

      const receiveOtherComponentDesignsPromise = mapAsync(
        Object.values(analysisSetGuidToComponentDesignForOtherInstances),
        ({ componentDesign }) =>
          dispatch(
            receiveComponentDesign(componentDesign, {
              commitComponentDesign: true,
              isPersisted: true,
              replaceWorkingChanges: true,
              skipJackGirderConnections: true,
            }),
          ),
        { concurrency: MAX_CONCURRENT_COMPONENT_DESIGN_LOADS },
      );

      await Promise.all([firstComponentToLoadPromise, receiveOtherComponentDesignsPromise]);

      return scheduleLineGroupData;
    } catch (error) {
      console.error(error);
      if (showSnackbarOnError) dispatch(enqueueError('Error loading schedule line group data'));
      return null;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

// A wrapper around `loadComponentDesign` intended for use during start up.
export const loadSingleComponentDesign =
  (componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Loading component...'));

    try {
      await dispatch(
        loadComponentDesign(componentDesignGuid, {
          skipJackGirderConnections: false,
        }),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

// A wrapper action for loading an analysis set as the basis for starting the application.
export const loadSingleComponentDesignFromAnalysisSet =
  (componentDesignGuid: string, analysisSetGuid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Loading component...'));

    try {
      // We still need to call loadComponentDesignByGuid so that the component's history and timestamps will be loaded.
      //   The component itself will be loaded from the analysis set below, so `skipSettingComponentDesign` must be set.
      await dispatch(
        loadComponentDesignByGuid(componentDesignGuid, {
          skipJackGirderConnections: true,
          skipSettingComponentDesign: true,
        }),
      );

      await dispatch(
        loadAnalysisSetByGuid(analysisSetGuid, {
          setAnalysisSetAsActive: true,
          skipJackGirderConnections: false,

          forceUpdateWorkingChanges: true,
        }),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

const loadActiveComponentDesignForCollection =
  (
    activeComponentDesignGuid: string,
    componentDesignGuids: string[],
    collectionType = 'collection',
  ): AsyncThunkActionVoid =>
  async (dispatch) => {
    const activeComponentDesignIsInCollection = componentDesignGuids.includes(activeComponentDesignGuid);

    if (!activeComponentDesignIsInCollection && activeComponentDesignGuid !== '0' && collectionType !== 'job') {
      dispatch(enqueueWarning(`The component ID supplied in the URL is not part of this ${collectionType}.`));
    }

    const componentDesignToLoadFirstGuid =
      (activeComponentDesignIsInCollection && activeComponentDesignGuid) || componentDesignGuids[0] || null;

    if (componentDesignToLoadFirstGuid == null) {
      return;
    }

    await dispatch(
      loadComponentDesign(componentDesignToLoadFirstGuid, {
        skipJackGirderConnections: true,
      }),
    );
  };

export const loadDimensionSet =
  (dimensionSetGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (dimensionSetGuid in getState().dimensions.sets) return;
    const dimensionSet = await designService.dimensions.getDimensionSet(dimensionSetGuid);
    dispatch(setDimensionSets(dimensionSet));
  };

// Does not persist the dimensions
export const loadStandardDimensions =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch) => {
    const { guid, component } = componentDesign;
    const dimensionSet = await designService.dimensions.getStandardDimensions(component);
    dispatch(setDimensionSets(dimensionSet));
    dispatch(
      setDimensionSetGuid({
        componentDesignGuid: guid,
        dimensionSetGuid: dimensionSet.guid,
      }),
    );
  };

export const loadAdditionalComponentDesignInformation =
  (componentDesign: ComponentDesign, options: { isPersisted: boolean }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const shouldLoadPrices =
      !!getLoggedInUser(getState()) && !isLinkUser(getState()) && hasPricingPermission(getState());
    await Promise.all([
      dispatch(loadAdditionalMemberInformation(componentDesign.component.members)),
      dispatch(loadAdditionalPlatePairInformation(componentDesign.component.platePairs)),
      options.isPersisted && componentDesign.loadSetGuid != null
        ? dispatch(loadLoadSet(componentDesign.loadSetGuid))
        : Promise.resolve(),
      shouldLoadPrices ? dispatch(loadLumberPricesForComponentDesign(componentDesign)) : Promise.resolve(),
      shouldLoadPrices ? dispatch(loadPlatePrices(componentDesign)) : Promise.resolve(),
      shouldLoadPrices ? dispatch(loadHangerPrices(componentDesign)) : Promise.resolve(),
      dispatch(loadPlateLateralResistanceCheck(componentDesign)),
    ]);
  };

interface ReceiveComponentDesignOptions {
  readonly commitComponentDesign: boolean;
  /**
   * `true` if the component should be set as the current editable version of the component, `false` if we are
   * previewing an old component.
   */
  readonly replaceWorkingChanges: boolean;
  readonly isPersisted: boolean;

  readonly skipJackGirderConnections: boolean;
  /**
   * Useful when the component design hasn't been added to the project yet or for shared component sets where the
   * settings are loaded separately
   */
  readonly skipSettings?: boolean;
}

export const receiveComponentDesign =
  (componentDesign: ComponentDesign, options: ReceiveComponentDesignOptions): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    try {
      let state = getState();

      const componentDesignIsNew = !(componentDesign.guid in state.componentDesigns.data);

      if (options.replaceWorkingChanges || componentDesignIsNew) {
        dispatch(
          setComponentDesignWithoutInvalidation({
            componentDesign,
            loadedState: { committed: options.commitComponentDesign, fullyLoaded: true },
          }),
        );
      }

      await Promise.all([
        dispatch(loadAdditionalComponentDesignInformation(componentDesign, { isPersisted: options.isPersisted })),
        !options.skipJackGirderConnections
          ? dispatch(fetchAndLoadJackGirderConnections(componentDesign.guid))
          : Promise.resolve(),
        options.isPersisted ? dispatch(loadDimensionSet(componentDesign.dimensionSetGuid)) : Promise.resolve(),
        options.isPersisted && !options.skipSettings
          ? dispatch(loadLoadSettings(componentDesign.guid))
          : Promise.resolve(),
        // The analysis settings are needed to show the camber in the component properties panel, and the multi-ply
        // connection type if applicable
        options.isPersisted && !options.skipSettings
          ? dispatch(loadAnalysisSettings(componentDesign.guid))
          : Promise.resolve(),
      ]);

      if (options.isPersisted) {
        const componentDesignLayoutModel = componentDesignToLayoutModel(componentDesign);
        dispatch(nonSynchronizedLayoutModelLoaded(componentDesignLayoutModel));
      }

      state = getState();
      const {
        selections: { activeComponentDesignGuid },
      } = state;
      const selectedEntry = getSingleSelectedItemEntryOrNull(state);

      if (
        componentDesignIsNew &&
        componentDesign.guid === activeComponentDesignGuid &&
        selectedEntry &&
        isConsideredDesignItem(selectedEntry.type)
      ) {
        dispatch(selectItem(selectedEntry.item, selectedEntry.type, { force: true }));
        focusCameraOnSelectedObjects();
      }

      state = getState();
      if (shouldSynchronizeComponent(state)) {
        getSyncClient().whenReady.then((client) => client.rooms.enter(`#design-${componentDesign.guid}`));
      }
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Could not load additional component information ${componentDesign.guid}.`));
    }
  };

interface ReceiveAnalysisSetOptions {
  /**
   * True if the analysis set's component should be set as the current editable version of the component, false if we
   * are merely previewing an old analysis set.
   */
  readonly replaceWorkingChanges: boolean;

  /**
   * Set this to false if you don't want the active analysis set to be modified by this action (for example, if you have
   * already manually set it or will soon).
   */
  readonly setAnalysisSetAsActive: boolean;

  readonly skipJackGirderConnections: boolean;
  readonly skipSettings?: boolean;
}

export const receiveAnalysisSet =
  (analysisSet: AnalysisSet, options: ReceiveAnalysisSetOptions): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { replaceWorkingChanges, setAnalysisSetAsActive, skipJackGirderConnections, skipSettings } = options;

    if (replaceWorkingChanges) {
      await dispatch(
        receiveComponentDesign(analysisSet.componentDesign, {
          replaceWorkingChanges,
          commitComponentDesign: true,
          isPersisted: true,
          skipJackGirderConnections,
          skipSettings,
        }),
      );
    } else {
      await dispatch(loadAdditionalComponentDesignInformation(analysisSet.componentDesign, { isPersisted: true }));
    }

    dispatch(analysisSetsPush(analysisSet));

    const componentDesignIsActive = analysisSet.componentDesign.guid === getActiveComponentDesign(getState())?.guid;
    if (componentDesignIsActive && setAnalysisSetAsActive) {
      dispatch(analysisSetsSetActiveGuid(analysisSet.guid));
    }

    dispatch(selectLoadCaseInUrlOrDefault());
  };

const loadAdditionalMemberInformation =
  (members: ReadonlyArray<Member>): AsyncThunkActionVoid =>
  async (dispatch) => {
    const lumbers = members.map((member) => member.lumber);
    const uniqueLumbers = uniqWith(
      lumbers,
      (lumber1, lumber2) =>
        lumber1.species === lumber2.species &&
        lumber1.grade === lumber2.grade &&
        lumber1.nominalWidth === lumber2.nominalWidth,
    );
    await Promise.all(uniqueLumbers.map((lumber) => dispatch(loadAdditionalLumberInformation(lumber))));
  };

export const loadLoadSet =
  (guid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    if (state.loadSets[guid] == null) {
      const loadSet = await designService.loadSets.get(guid);
      dispatch(setLoadSets(loadSet));
    }
  };

export const loadSettings =
  (modelGuid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    await Promise.all([dispatch(loadAnalysisSettings(modelGuid)), dispatch(loadLoadSettings(modelGuid))]);
  };

export const loadAnalysisSettingsBulk =
  (modelGuids: readonly string[]): AsyncThunkActionReturn<{ [modelGuid: string]: AnalysisSettings }> =>
  async (dispatch, getState) => {
    const [loadedModelGuids, unloadedModelGuids] = partition(
      modelGuids,
      (modelGuid) => !!getState().settingsDatabase.analysisSettings[modelGuid],
    );
    const analysisSettingsByModelGuid = await designService.settings.getModelAnalysisSettingsBulk(unloadedModelGuids);

    dispatch(
      setAnalysisSettingsFromObject(
        pickBy(analysisSettingsByModelGuid, (_, modelGuid) => modelGuid in getState().componentDesigns.data),
      ),
    );

    for (const loadedModelGuid of loadedModelGuids) {
      analysisSettingsByModelGuid[loadedModelGuid] = getState().settingsDatabase.analysisSettings[loadedModelGuid];
    }

    return analysisSettingsByModelGuid;
  };

export const loadAnalysisSettings =
  (modelGuid: string): AsyncThunkActionReturn<AnalysisSettings> =>
  async (dispatch, getState) => {
    let analysisSettings = getAnalysisSettings(getState(), modelGuid);
    if (!analysisSettings) {
      analysisSettings = await designService.settings.getModelAnalysisSettings(modelGuid);
      dispatch(setAnalysisSettingsForComponentDesigns(modelGuid, analysisSettings));
    }

    return analysisSettings;
  };

export const loadLoadSettingsBulk =
  (modelGuids: readonly string[]): AsyncThunkActionReturn<{ [modelGuid: string]: LoadSettings }> =>
  async (dispatch, getState) => {
    const [loadedModelGuids, unloadedModelGuids] = partition(
      modelGuids,
      (modelGuid) => !!getState().settingsDatabase.loadSettings[modelGuid],
    );
    const loadSettingsByModelGuid = await designService.settings.getModelLoadSettingsBulk(unloadedModelGuids);

    dispatch(
      setLoadSettingsFromObject(
        pickBy(loadSettingsByModelGuid, (_, modelGuid) => modelGuid in getState().componentDesigns.data),
      ),
    );

    for (const loadedModelGuid of loadedModelGuids) {
      loadSettingsByModelGuid[loadedModelGuid] = getState().settingsDatabase.loadSettings[loadedModelGuid];
    }

    return loadSettingsByModelGuid;
  };

export const loadLoadSettings =
  (modelGuid: string): AsyncThunkActionReturn<LoadSettings> =>
  async (dispatch, getState) => {
    let loadSettings = getLoadSettings(getState(), modelGuid);
    if (!loadSettings) {
      loadSettings = await designService.settings.getModelLoadSettings(modelGuid);
      dispatch(setLoadSettingsForComponentDesigns(modelGuid, loadSettings));
    }

    return loadSettings;
  };

export const loadMaterialSettings =
  (modelGuid: string): AsyncThunkActionReturn<MaterialSettingsPair> =>
  async (dispatch, getState) => {
    let materialSettings = getState().settingsDatabase.materialSettings[modelGuid];
    if (!materialSettings) {
      materialSettings = await designService.settings.getModelMaterialSettings(modelGuid);
      dispatch(setMaterialSettingsForComponentDesigns(modelGuid, materialSettings));
    }

    return materialSettings;
  };

export const loadMaterialSettingsWithSpinner =
  (modelGuid: string): AsyncThunkActionReturn<MaterialSettingsPair | null> =>
  async (dispatch) => {
    const materialSettingsPromise = dispatch(loadMaterialSettings(modelGuid));

    // Usually, settings will already exist in redux state or will load very fast (on a good connection). So we avoid
    //   showing a loading spinner unless it's taking a while to load the settings.

    let cleanupSpinner = () => {};
    const spinnerTimeout = setTimeout(() => {
      const spinnerId = dispatch(addSpinnerMessage('Loading material settings...'));
      cleanupSpinner = () => dispatch(removeSpinnerMessage(spinnerId));
    }, 200);

    try {
      const materialSettings = await materialSettingsPromise;
      clearTimeout(spinnerTimeout);
      return materialSettings;
    } catch {
      dispatch(enqueueError('Error loading material settings.'));
      return null;
    } finally {
      cleanupSpinner();
    }
  };

export const loadMaterialSettingsAndPrices =
  (modelGuid: string): AsyncThunkActionReturn<MaterialSettingsPair> =>
  async (dispatch) => {
    const materialSettings = await dispatch(loadMaterialSettings(modelGuid));
    await dispatch(loadLumberPricesForMaterialSettings(materialSettings));
    return materialSettings;
  };

export const loadDefaultMaterialSettingsAndPrices =
  (): AsyncThunkActionReturn<MaterialSettingsPair> => async (dispatch) => {
    const defaultMaterialSettings = await designService.defaults.getDefaultMaterialSettings();
    await dispatch(loadLumberPricesForMaterialSettings(defaultMaterialSettings));
    return defaultMaterialSettings;
  };

export const loadFeaturePatternSettings =
  (modelGuid: string): AsyncThunkActionReturn<FeaturePatternTrussSettings> =>
  async (dispatch, getState) => {
    let featurePatternSettings = getState().settingsDatabase.featurePatternSettings[modelGuid];
    if (!featurePatternSettings) {
      try {
        featurePatternSettings = await designService.settings.getModelFeaturePatternSettings(modelGuid);
      } catch (err) {
        if ((err as AspNetStatusCodeResult).status === 404) {
          featurePatternSettings =
            await designService.componentDesigns.getDefaultFeaturePatternTrussSettings('default');
        } else {
          throw err;
        }
      }

      dispatch(setFeaturePatternSettingsForComponentDesigns(modelGuid, featurePatternSettings));
    }

    return featurePatternSettings;
  };

export const loadProfileDefaults =
  (modelGuid: string): AsyncThunkActionReturn<ProfileDefaults> =>
  async (dispatch, getState) => {
    let profileDefaults = getState().settingsDatabase.profileDefaults[modelGuid];

    if (!profileDefaults) {
      profileDefaults = await designService.settings.getModelProfileDefaults(modelGuid);
      dispatch(setProfileDefaultsForComponentDesigns(modelGuid, profileDefaults));
    }

    return profileDefaults;
  };

// Created for use in cases where we don't actually know the correct model guid for which to fetch profile defaults.
//   Using the project settings and falling back to the default defaults was deemed good enough for now.
//
// EVERY USAGE of this action is an oppertunity for incorrect profile settings to be used. Ideally we figure out how to
//   fix each case and then remove this action.
export const __UNSAFE_loadProfileDefaultsUsingProjectGuidOrFallback =
  (): AsyncThunkActionReturn<ProfileDefaults> => async (dispatch, getState) => {
    const state = getState();
    const context = state.application.openContext;

    const projectGuid = context?.type === 'project' ? context.projectGuid : null;

    return projectGuid
      ? dispatch(loadProfileDefaults(projectGuid))
      : designService.defaults.getDefaultProfileDefaults();
  };

export const loadLumberPricesForComponentDesign =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch) => {
    const lumbers = componentDesign.component.members.map((member) => member.lumber);
    await dispatch(loadLumberPrices(lumbers));
  };

export const loadLumberPricesForMaterialSettings =
  (materialSettings: MaterialSettingsPair): AsyncThunkActionVoid =>
  async (dispatch) => {
    const { profileMaterialSettings, featurePatternMaterialSettings } = materialSettings;

    const lumbers = [
      ...(profileMaterialSettings
        ? Object.values(pick(profileMaterialSettings, materialSettingsLumberProperties))
        : []),

      ...(featurePatternMaterialSettings
        ? Object.values(pick(featurePatternMaterialSettings, materialSettingsLumberProperties))
        : []),
    ];

    await dispatch(loadLumberPrices(lumbers));
  };

export const loadLumberPrices =
  (lumbers: readonly Lumber[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const lumberMissingPrices = uniqWith(lumbers, lumbersAreEqual).filter((lumber) => {
      const lumberCode = getLumberCode(lumber);
      return !(lumberCode in getState().inventoryPrices.lumberPriceRequests);
    });

    if (!lumberMissingPrices.length) return;

    await Promise.all(
      lumberMissingPrices.map(async (lumber) => {
        dispatch(setLumberPriceRequested(lumber));
        const lumberPrices = await designService.lumberPrices.getMostRecentLumberPrices(lumber);
        const stockLengthPrices = lumberPrices.map((lumberPrice) => ({
          stockLength: lumberPrice.lengthInInches,
          price: lumberPrice.pricePerThousandBoardFeet,
        }));
        dispatch(setPricesForLumber({ lumber, stockLengthPrices }));
      }),
    );
  };

const loadPlatePrices =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const uniquePlateTypes = uniq(
      componentDesign.component.platePairs
        .map((platePair) => platePair.plateType)
        .filter((plateType) => !isComboPlateType(plateType)),
    );

    await Promise.all(
      uniquePlateTypes.map(async (plateType) => {
        if (getState().inventoryPrices.platePriceRequests[plateType] != null) return;

        dispatch(setPlatePriceRequested(plateType));

        const platePrice = await designService.platePrices.getMostRecentPlatePrice(plateType);
        if (platePrice) dispatch(setPricePerPoundForPlateType({ plateType, pricePerPound: platePrice.pricePerPound }));
      }),
    );
  };

const loadHangerPrices =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const uniqueStandardHangerModels = uniq(
      componentDesign.component.hangers
        .map((hanger) => hanger.connector)
        .filter((connector) => connector.standardOrCustom === 'Standard')
        .map((connector) => connector.model),
    );

    await Promise.all(
      uniqueStandardHangerModels.map(async (hanger) => {
        if (getState().inventoryPrices.hangerPriceRequests[hanger] != null) return;

        dispatch(setHangerPriceRequested(hanger));

        const hangerPrice = await designService.hangerPrices.getMostRecentHangerPrice(hanger);
        if (hangerPrice) dispatch(setPriceForHanger({ hanger, price: hangerPrice.price }));
      }),
    );
  };
